import { useCallback, useState, type DragEvent, type ReactNode } from 'react'

export type DropZoneProps = {
  accept: string[]
  maxSizeMB?: number
  disabled?: boolean
  onFile: (file: File) => void
  onError?: (message: string) => void
  children: (state: { isDragging: boolean }) => ReactNode
}

/**
 * Render-prop component for native HTML5 file drag-and-drop.
 * Validates MIME type and file size on drop, provides isDragging
 * state for visual feedback. Click-to-browse is the consumer's
 * responsibility (pair with a hidden input + button inside children).
 */
export const DropZone = ({ accept, maxSizeMB = 10, disabled = false, onFile, onError, children }: DropZoneProps) => {
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const [, setDragCounter] = useState<number>(0)

  const handleDragEnter = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      if (disabled) return
      setDragCounter((c) => {
        if (c === 0) setIsDragging(true)
        return c + 1
      })
    },
    [disabled],
  )

  const handleDragLeave = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      if (disabled) return
      setDragCounter((c) => {
        const next = c - 1
        if (next <= 0) {
          setIsDragging(false)
          return 0
        }
        return next
      })
    },
    [disabled],
  )

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)
      setDragCounter(0)
      if (disabled) return

      const items = Array.from(e.dataTransfer.items)
      const fileItem = items.find((item) => item.kind === `file`)
      if (!fileItem) return

      const file = fileItem.getAsFile()
      if (!file) return

      if (!accept.includes(file.type)) {
        onError?.(`File type "${file.type}" is not accepted`)
        return
      }

      if (file.size > maxSizeMB * 1024 * 1024) {
        onError?.(`File exceeds ${maxSizeMB}MB limit`)
        return
      }

      onFile(file)
    },
    [accept, disabled, maxSizeMB, onFile, onError],
  )

  return (
    <div onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDragOver={handleDragOver} onDrop={handleDrop}>
      {children({ isDragging: !disabled && isDragging })}
    </div>
  )
}
